import * as moment from "moment";
import * as React from "react";
import DateUtils from "sh-application/utility/DateUtils";
import StringsStore from "sh-strings/store";
import { DatePicker, DatePickerProps, CalendarStrings } from "@fluentui/react-datepicker-compat";
import { Moment } from "moment";

interface LocalizedDatePickerV2Props extends Omit<DatePickerProps, 'value' | 'onSelectDate' | 'formatDate' | 'parseDateFromString' | 'strings'> {
    title?: string;
    datePickerClassName?: string;
    value: moment.Moment;
    timeZoneOlsonCode: string;
    onDateSelectedCallBack: (date: moment.Moment) => void;
    ariaLabelDate?: string;
    isRequired?: boolean;
    disabled?: boolean;
}

export const LocalizedDatePickerV2: React.FC<LocalizedDatePickerV2Props> = ({
    title,
    datePickerClassName,
    value,
    timeZoneOlsonCode,
    onDateSelectedCallBack,
    ariaLabelDate,
    isRequired,
    disabled,
    ...datePickerProps
}) => {
    const [isEmpty, setIsEmpty] = React.useState<boolean>(false);
    const teamsettingsStrings = StringsStore().registeredStringModules.get("teamsettings")?.strings;

    // Get locale data from moment for localized strings
    const localeData = moment.localeData();
    const currDateFormat = localeData.longDateFormat("L");

    // Prepare localized calendar strings
    const calendarStrings: CalendarStrings = React.useMemo(
        () => ({
            months: localeData.months(),
            shortMonths: localeData.monthsShort(),
            days: localeData.weekdays(),
            shortDays: localeData.weekdaysShort(),
            goToToday: teamsettingsStrings?.get("goToToday") || "Go to today",
            prevMonthAriaLabel: teamsettingsStrings?.get("goToPreviousMonth") || "Previous month",
            nextMonthAriaLabel: teamsettingsStrings?.get("goToNextMonth") || "Next month",
            prevYearAriaLabel: teamsettingsStrings?.get("goToPreviousYear") || "Previous year",
            nextYearAriaLabel: teamsettingsStrings?.get("goToNextYear") || "Next year"
        }),
        [localeData, teamsettingsStrings]
    );

    // Convert timezone date to display date
    const displayDateValue: Date | null = React.useMemo(() => {
        if (!value) {
            return null;
        }
        return timeZoneOlsonCode ? DateUtils.convertTimezoneDateTimeToDisplayTime(value.toDate(), timeZoneOlsonCode) : value.toDate();
    }, [value, timeZoneOlsonCode]);

    // Handle date selection
    const onDateChanged = React.useCallback(
        (date: Date | null | undefined) => {
            setIsEmpty(!date);

            if (date && onDateSelectedCallBack) {
                date.setHours(0, 0, 0, 0); // Clear the time portion

                // Convert display time back to timezone
                const dateSelectedInTimeZone = DateUtils.convertDisplayTimeToTimezoneDateTime(date, timeZoneOlsonCode);
                onDateSelectedCallBack(moment(dateSelectedInTimeZone).tz(timeZoneOlsonCode));
            }
        },
        [onDateSelectedCallBack, timeZoneOlsonCode]
    );

    // Handle blur event for required validation
    const onLostFocus = React.useCallback(() => {
        if (isRequired && isEmpty && value) {
            // Reset to the stored value if empty and required
            onDateChanged(value.toDate());
        }
    }, [isRequired, isEmpty, value, onDateChanged]);

    // Format date for display
    const formatDate = React.useCallback(
        (date: Date): string => {
            if (!date) {
                return "";
            }
            return DateUtils.convertDisplayTimeToTimezoneMoment(date, timeZoneOlsonCode).format(currDateFormat);
        },
        [timeZoneOlsonCode, currDateFormat]
    );

    // Parse date string
    const parseDateFromString = React.useCallback(
        (dateStr: string): Date | null => {
            // When an invalid date value is entered manually, return the previously selected date value
            let returnDate: Moment = value ? value.clone() : moment();

            if (dateStr && dateStr.length > 0) {
                const parsedDate: Moment = moment(dateStr, currDateFormat);
                if (parsedDate && !isNaN(parsedDate.valueOf())) {
                    returnDate = parsedDate.clone();
                }
            }

            // Convert to display date
            const displayDate: Date =
                timeZoneOlsonCode && returnDate
                    ? DateUtils.convertTimezoneDateTimeToDisplayTime(returnDate.toDate(), timeZoneOlsonCode)
                    : returnDate.toDate();

            return displayDate;
        },
        [value, currDateFormat, timeZoneOlsonCode]
    );

    // Placeholder text
    const placeholder = moment().tz(timeZoneOlsonCode).format(currDateFormat);

    return (
        <DatePicker
            {...datePickerProps}
            disabled={disabled}
            className={datePickerClassName}
            allowTextInput
            value={displayDateValue}
            onSelectDate={onDateChanged}
            placeholder={placeholder}
            formatDate={formatDate}
            parseDateFromString={parseDateFromString}
            onBlur={onLostFocus}
            aria-label={ariaLabelDate}
            strings={calendarStrings}
        />
    );
};

export default LocalizedDatePickerV2;
